import {
  FormStoppageTypeWithDepartments,
  Iso8601,
  StoppageDto,
  Stoppage,
  StoppageKind
} from '@hconnect/common/types'
import {getTranslationKey} from '@hconnect/common/utils'
import {shouldNotBeEmpty, Validator} from '@hconnect/common/validators'
import {TFunction} from 'i18next'
import moment from 'moment-timezone'

import {
  isStoppageTypeForNewWithCodeOldWithReason,
  isStoppageTypeWithCode,
  isStoppageTypeWithReason
} from '../utils'

const COST_PATTERN = /^(?!0\.00)(?:\d*\.\d{2}|\d+)$/

export const requiredEquipmentNumberValidator: Validator<StoppageDto> = (value: string, item) => {
  return item.eventType !== StoppageKind.PlannedStoppage && !item.equipment
    ? 'shouldBe.notEmpty'
    : undefined
}

const formStoppageTypeWithDepartmentsValues: readonly string[] = Object.values(
  FormStoppageTypeWithDepartments
)
export const requiredStoppedMainEquipmentNumberValidator: Validator<StoppageDto> = (
  value: string,
  item
) => {
  return item.stoppageType &&
    formStoppageTypeWithDepartmentsValues.includes(item.stoppageType) &&
    !item.upmDepartmentId
    ? 'shouldBe.notEmpty'
    : undefined
}

const stoppageValidatorWithCodeOldWithReason = (item: Partial<StoppageDto>) => {
  const value = item['stoppageCode'] || item['stoppageReason']
  return value && value.trim().length > 0 ? undefined : 'shouldBe.notEmpty'
}

export const stoppageCodeValidator: Validator<StoppageDto> = (
  value: string,
  item: Partial<StoppageDto>
) => {
  if (item.stoppageType && isStoppageTypeForNewWithCodeOldWithReason(item.stoppageType)) {
    return stoppageValidatorWithCodeOldWithReason(item)
  }
  if (item.stoppageType && isStoppageTypeWithCode(item.stoppageType)) {
    return value && value.trim().length > 0 ? undefined : 'shouldBe.notEmpty'
  }
}

export const stoppageReasonValidator: Validator<StoppageDto> = (
  value: string,
  item: Partial<StoppageDto>
) => {
  if (item.stoppageType && isStoppageTypeForNewWithCodeOldWithReason(item.stoppageType)) {
    return stoppageValidatorWithCodeOldWithReason(item)
  }
  if (item.stoppageType && isStoppageTypeWithReason(item.stoppageType)) {
    return value && value.trim().length > 0 ? undefined : 'shouldBe.notEmpty'
  }
}

export const costValidator: Validator<StoppageDto> = (
  value: string,
  item: Partial<StoppageDto>
) => {
  const emptyValidation = shouldNotBeEmpty<StoppageDto>()(value, item)
  if (emptyValidation) {
    return emptyValidation
  }
  const isValidFormat = COST_PATTERN.test(value)
  if (!isValidFormat) {
    return 'shouldBe.validCostFormat'
  }
}

export const validFromValidator: Validator<StoppageDto> = (
  date: Iso8601 | undefined,
  item: Partial<StoppageDto>
) => {
  if (date && item.eventType !== StoppageKind.PlannedStoppage && moment(date).isAfter(moment())) {
    return 'shouldBe.notInFutureDate'
  }
  return shouldNotBeEmpty<StoppageDto>()(date, item)
}

export const validToValidator: Validator<StoppageDto> = (
  date: Iso8601 | undefined,
  item: Partial<StoppageDto>
) => {
  if (date && item.eventType !== StoppageKind.PlannedStoppage && moment(date).isAfter(moment())) {
    return 'shouldBe.notInFutureDate'
  }
  if (date && 'stoppageStart' in item && !moment(date).isAfter(item.stoppageStart)) {
    return 'shouldBe.dateAfter'
  }
  return undefined
}

export const stoppageOverlappingValidator = (
  t: TFunction,
  existingEvent?: Stoppage,
  translationPrefix?: string
) => {
  if (existingEvent) {
    const dateFormat = 'L'
    const startDate = existingEvent.stoppageStart.format(dateFormat)
    const endDate = existingEvent.stoppageEnd?.format(dateFormat)

    return endDate
      ? t(getTranslationKey('shouldBe.notOverlapping', translationPrefix), {startDate, endDate})
      : t(getTranslationKey('shouldBe.notOverlappingOngoing', translationPrefix), {startDate})
  }
}

export const validFromValidatorWithOverlapCheck =
  (
    t: TFunction,
    existingEvent?: Stoppage,
    translationPrefix?: string,
    skipOverlapValidation?: boolean
  ): Validator<StoppageDto> =>
  (date: Iso8601 | undefined, item: Partial<StoppageDto>) => {
    const validFromValidation = validFromValidator(date, item)
    const validFromMessage =
      validFromValidation && t(getTranslationKey(validFromValidation, translationPrefix))
    return existingEvent && !skipOverlapValidation
      ? validFromMessage || stoppageOverlappingValidator(t, existingEvent, translationPrefix)
      : validFromMessage
  }

export const validToValidatorWithOverlapCheck =
  (
    t: TFunction,
    existingEvent?: Stoppage,
    translationPrefix?: string,
    skipOverlapValidation?: boolean
  ): Validator<StoppageDto> =>
  (date: Iso8601 | undefined, item: Partial<StoppageDto>) => {
    const validToValidation = validToValidator(date, item)
    const validToMessage =
      validToValidation && t(getTranslationKey(validToValidation, translationPrefix))
    return item.stoppageEnd && existingEvent && !skipOverlapValidation
      ? validToMessage || stoppageOverlappingValidator(t, existingEvent, translationPrefix)
      : validToMessage
  }
